<template>
  <Form @submit="submit" :title="$t('meta.baseData')" bodyClass="">
    <FormInput :name="$t('areas.name')" v-model="area.name" required />
    <FormInput :name="$t('areas.externalId')" v-model="area.external_id" labelOptional />
    <FormInput :name="$t('areas.description')" v-model="area.description" labelOptional />

    <div v-show="fractions.length > 0" class="form-wrap">
      <span class="label">{{ $t('meta.fractions') }}</span>

      <div>
        <label for="selectable-fractions-all" style="display: inline">
          <input
            type="radio"
            id="selectable-fractions-all"
            name="coloring"
            value="all"
            v-model="selectableFractionsToggle"
          />
          {{ $t('areas.selectableFractions.all') }}
        </label>

        <label for="selectable-fractions-some" style="display: inline; margin-left: 10px">
          <input
            type="radio"
            id="selectable-fractions-some"
            name="coloring"
            value="some"
            v-model="selectableFractionsToggle"
          />
          {{ $t('areas.selectableFractions.some') }}
        </label>
      </div>

      <div v-show="selectableFractionsToggle == 'some'" class="scroll-box">
        <label v-for="fraction in fractions" :key="fraction.uuid" class="checkbox-wrap" style="margin-top: 0.5rem">
          <input type="checkbox" :value="fraction.uuid" v-model="selectableFractionUuids" />
          {{ fraction.name }}
        </label>
      </div>
    </div>

    <BaseMap
      :allowFourNewCoordinatesOnly="isNew"
      :editablePolygonFeature="geojsonFeature"
      :staticPolygons="staticPolygonsForMap"
      :fitBounds="(areaPolygon || sitePolygon).properties.bbox"
      :parentPolygon="sitePolygon"
      :childPolygons="zonesPolygons"
      :enableDraw="isNew"
      :zoomedOut="isNew"
      @coordinates-updated="handleCoordinatesUpdate"
      @coordinates-reset="resetCoordinates"
      enableGeocoder
    />
    <div class="form-wrap">
      <span class="label">{{ $t('meta.sitePlan') }} {{ `(${$t('meta.optional')})` }}</span>
      <ImageUpload v-model="area.image" :label="$t('imageUpload.add.area')" />
    </div>
    <Button type="submit" :disabled="submitDisabled">{{ isNew ? $t('areas.createArea') : $t('areas.update') }}</Button>
  </Form>
</template>

<script>
import Button from '@/components/Button/index.vue'
import ImageUpload from '@/components/Form/ImageUpload.vue'
import FormInput from '@/components/Form/Input/index.vue'
import Form from '@/components/Form/index.vue'
import BaseMap from '@/components/Map/base.vue'

import fractionsApi from '@/api/companies/fractions'
import { staticPolygonsFromObject } from '@/utils/geojson'
import mapUtils from '@/utils/mapbox'

export default {
  inject: ['companyUser'],
  data() {
    return {
      area: this.initialArea || {
        image: {},
        company_uuid: this.companyUser.company_uuid,
        name: null,
        mb_feature_polygon: null,
        lat: null,
        lng: null,
        selectable_fractions: [],
      },
      fractions: [],
      selectableFractionUuids: [],
      selectableFractionsToggle: null,
    }
  },
  props: {
    initialArea: { type: Object },
    site: { type: Object, required: true },
    zones: { type: Array, default: () => [] },
  },
  components: {
    Button,
    Form,
    FormInput,
    ImageUpload,
    BaseMap,
  },
  methods: {
    handleCoordinatesUpdate(newCoordinates) {
      const center = mapUtils.calculateCenterOfCoordinates(mapUtils.coordinatePairArrayToLngLatObject(newCoordinates))
      this.area.mb_feature_polygon = JSON.stringify({
        type: 'FeatureCollection',
        features: [
          {
            id: this.area.uuid,
            type: 'Feature',
            properties: {},
            geometry: {
              coordinates: [newCoordinates],
              type: 'Polygon',
            },
          },
        ],
      })

      this.area.lat = center.lat
      this.area.lng = center.lng
    },
    resetCoordinates() {
      this.area.lat = null
      this.area.lng = null
    },
    submit() {
      if (this.selectableFractionsToggle === 'all') {
        this.area.selectable_fraction_uuids = []
      } else {
        this.area.selectable_fraction_uuids = this.selectableFractionUuids
      }

      this.$emit('submit', this.area)
    },
  },
  computed: {
    isNew() {
      return !this.area.uuid
    },
    submitDisabled() {
      const { name, mb_feature_polygon, lat, lng } = this.area
      return !name || !mb_feature_polygon || !lat || !lng
    },
    staticPolygonsForMap() {
      return [
        this.sitePolygon,
        ...(this.site.areas?.map(area => staticPolygonsFromObject('area', area)) || []),
        ...this.zonesPolygons,
      ].filter(polygon => polygon)
    },
    areaPolygon() {
      if (this.isNew) return null
      return staticPolygonsFromObject('area', this.area)
    },
    sitePolygon() {
      return staticPolygonsFromObject('site', this.site)
    },
    zonesPolygons() {
      return this.zones?.map(zone => staticPolygonsFromObject('zone', zone)) || []
    },
    geojsonFeature() {
      if (this.isNew) return null
      return JSON.parse(this.area.mb_feature_polygon).features[0]
    },
  },
  async mounted() {
    this.fractions = await fractionsApi.getAll(this.companyUser.company_uuid, { selectable_in_orders: 'true' })
    this.selectableFractionUuids = this.area.selectable_fractions.map(fraction => fraction.uuid)

    // No need to submit this later
    delete this.area.selectable_fractions

    if (this.selectableFractionUuids.length === 0) {
      this.selectableFractionsToggle = 'all'
    } else {
      this.selectableFractionsToggle = 'some'
    }
  },
}
</script>
<style lang="scss">
@import '@/components/Form/Checkbox/Checkbox.scss';

.scroll-box {
  margin-top: 0.5em;
  max-height: 300px;
  overflow-y: auto;

  border: 1px solid var(--vs-border-color);
  border-radius: 6px;
  padding: 5px 10px;
}
</style>
